import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/I18nProvider";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { useBackNavigation } from "@/hooks/useBackNavigation";
import { useState, useEffect } from "react";
import { getCmsLayout, CmsLayout } from "@/data/cmsStore";

const PourquoiPage = () => {
  const { t, isRtl, lang } = useI18n();
  const BackArrow = isRtl ? ArrowRight : ArrowLeft;
  const handleBack = useBackNavigation("/accueil-pourquoi");
  const [layout, setLayout] = useState<CmsLayout>([]);

  useEffect(() => {
    setLayout(getCmsLayout("pourquoi"));
    
    const handleLayoutChange = (e: any) => {
      if (e.detail?.page === "pourquoi") {
        setLayout(getCmsLayout("pourquoi"));
      }
    };
    window.addEventListener("cmsLayoutChanged", handleLayoutChange);
    return () => window.removeEventListener("cmsLayoutChanged", handleLayoutChange);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-24 pb-16">
        <div className="container max-w-4xl">
          <Link to="/accueil-pourquoi" onClick={handleBack} className="inline-flex items-center gap-2 text-sm font-body text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <BackArrow className="w-4 h-4" />
            {t.common.backHome}
          </Link>

          {layout.length > 0 ? (
            layout.map((section) => {
              if (!section.visible) return null;
              
              switch (section.type) {
                case "PourquoiHeroSection":
                  return (
                    <motion.div key={section.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                      <h1 className="text-3xl md:text-5xl font-display font-bold text-foreground mb-4">
                        {section.content[`title_${lang}`] || section.content.title_fr || t.why.pageTitle}
                      </h1>
                      <div 
                        className="text-lg text-muted-foreground font-body mb-16 max-w-2xl prose dark:prose-invert max-w-none"
                        dangerouslySetInnerHTML={{ __html: section.content[`subtitle_${lang}`] || section.content.subtitle_fr || t.why.pageSubtitle }}
                      />
                    </motion.div>
                  );

                case "PourquoiReasonsSection":
                  return (
                    <div key={section.id} className="space-y-10">
                      {t.why.detailedReasons.map((reason, i) => (
                        <motion.div
                          key={i}
                          className="bg-card rounded-2xl border border-border shadow-card p-8"
                          initial={{ opacity: 0, y: 30 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5, delay: i * 0.1 }}
                        >
                          <h2 className="text-2xl font-display font-bold text-foreground mb-3">
                            {reason.title}
                          </h2>
                          <div 
                            className="text-muted-foreground font-body text-lg mb-6 prose dark:prose-invert max-w-none"
                            dangerouslySetInnerHTML={{ __html: section.content[`reason${i+1}_desc_${lang}`] || section.content[`reason${i+1}_desc_fr`] || reason.description }}
                          />
                          <ul className="space-y-3">
                            {reason.details.map((detail, j) => (
                              <li key={j} className="flex items-start gap-3">
                                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                                <span className="text-foreground font-body text-sm leading-relaxed">{detail}</span>
                              </li>
                            ))}
                          </ul>
                        </motion.div>
                      ))}
                    </div>
                  );

                case "PourquoiCTASection":
                  return (
                    <motion.div
                      key={section.id}
                      className="mt-16 text-center"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.5 }}
                    >
                      <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Button size="lg" className="font-body font-semibold gap-2 text-lg px-8 py-6" asChild>
                          <Link to="/voter">{t.hero.voteBtn}</Link>
                        </Button>
                        <Button size="lg" variant="secondary" className="font-body font-semibold gap-2 text-lg px-8 py-6" asChild>
                          <Link to="/engagement">{t.hero.engageBtn}</Link>
                        </Button>
                      </div>
                    </motion.div>
                  );

                case "CustomRichSection":
                  const langHtml = section.content[`html_${lang}`] || section.content.html_fr;
                  return (
                    <div key={section.id} className={`${section.content.bg_color || 'bg-background'} ${section.content.padding_y || 'py-12'}`}>
                      <div dangerouslySetInnerHTML={{ __html: langHtml || '' }} />
                    </div>
                  );

                default:
                  return null;
              }
            })
          ) : (
            <div className="h-64 flex items-center justify-center">
              <span className="animate-pulse">Chargement...</span>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PourquoiPage;
